import type { ListParams, PaginatedResult } from '@/types/common'

interface QueryOptions<T> {
  /** Fields checked (case-insensitively, substring match) against params.search. */
  searchableFields: (keyof T)[]
  /** Fields allowed in params.sort.field; sorting on any other field is ignored. */
  sortableFields: (keyof T)[]
  /**
   * Optional custom matcher per filter key, for filters that aren't a plain
   * equality check (e.g. tag arrays, date ranges). Falls back to `item[key] === value`.
   */
  filterMatchers?: Partial<Record<keyof T, (item: T, value: unknown) => boolean>>
}

/**
 * One generic search → filter → sort → paginate implementation shared by every
 * domain mock service, so pagination/sort/filter logic is written exactly once.
 */
export function queryList<T extends Record<string, unknown>>(
  records: readonly T[],
  params: ListParams,
  options: QueryOptions<T>,
): PaginatedResult<T> {
  let result: T[] = [...records]

  if (params.search?.trim()) {
    const needle = params.search.trim().toLowerCase()
    result = result.filter((item) =>
      options.searchableFields.some((field) => {
        const value = item[field]
        return typeof value === 'string' && value.toLowerCase().includes(needle)
      }),
    )
  }

  if (params.filters) {
    for (const [key, value] of Object.entries(params.filters)) {
      if (value === undefined || value === null || value === '') continue
      if (Array.isArray(value) && value.length === 0) continue

      const matcher = options.filterMatchers?.[key as keyof T]
      result = result.filter((item) =>
        matcher ? matcher(item, value) : item[key as keyof T] === value,
      )
    }
  }

  if (params.sort && options.sortableFields.includes(params.sort.field as keyof T)) {
    const { field, order } = params.sort
    const direction = order === 'ascend' ? 1 : -1
    result = [...result].sort((a, b) => {
      const av = a[field as keyof T]
      const bv = b[field as keyof T]
      if (av === bv) return 0
      if (av === undefined || av === null) return 1
      if (bv === undefined || bv === null) return -1
      return av > bv ? direction : -direction
    })
  }

  const total = result.length
  const start = (params.page - 1) * params.pageSize
  const items = result.slice(start, start + params.pageSize)

  return { items, total, page: params.page, pageSize: params.pageSize }
}
